import { useEffect, useState } from "react";
import type { ComponentType } from "react";
import type { PaletteMode } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AppsIcon from "@mui/icons-material/Apps";
import HubIcon from "@mui/icons-material/Hub";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import TuneIcon from "@mui/icons-material/Tune";

import type { Client } from "./types";
import { ArqReview } from "./views/ArqReview";
import { Catalog } from "./views/Catalog";
import { Config } from "./views/Config";
import { Preferences } from "./views/Preferences";
import { Workflows } from "./views/Workflows";

const DRAWER_WIDTH = 264;

type NavSection = "workspace" | "administration" | "settings";

interface Tab {
  id: string;
  label: string;
  View: ComponentType<{ client: Client }>;
  icon: typeof AppsIcon;
  section: NavSection;
  adminOnly?: boolean;
}

const TABS: Tab[] = [
  { id: "catalog", label: "Apps", View: Catalog, icon: AppsIcon, section: "workspace" },
  {
    id: "workflows",
    label: "Workflows",
    View: Workflows,
    icon: AccountTreeIcon,
    section: "workspace",
  },
  {
    id: "arq",
    label: "Review queue",
    View: ArqReview,
    icon: AdminPanelSettingsIcon,
    section: "administration",
    adminOnly: true,
  },
  {
    id: "preferences",
    label: "Preferences",
    View: Preferences,
    icon: TuneIcon,
    section: "settings",
  },
  {
    id: "config",
    label: "Config",
    View: Config,
    icon: SettingsSuggestIcon,
    section: "settings",
  },
];

interface AppProps {
  client: Client;
  initialAuthed?: boolean;
  initialAdmin?: boolean;
  colorMode?: PaletteMode;
  onToggleColorMode?: () => void;
}

export function App({
  client,
  initialAuthed = false,
  initialAdmin = false,
  colorMode = "light",
  onToggleColorMode,
}: AppProps) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const [authed, setAuthed] = useState(initialAuthed);
  const [isAdmin, setIsAdmin] = useState(initialAdmin);
  const [tab, setTab] = useState("catalog");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    Promise.resolve(client.auth.isAuthenticated()).then(setAuthed);
    const identity = client.getIdentity && client.getIdentity();
    setIsAdmin(Boolean(identity && identity.isAdmin));
  }, [client]);

  if (!authed) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", px: 2, py: 6 }}>
        <Paper elevation={1} sx={{ width: "100%", maxWidth: 460, p: { xs: 3.5, sm: 5 } }}>
          <Stack spacing={3} alignItems="flex-start">
            <Avatar
              variant="rounded"
              sx={{ width: 52, height: 52, bgcolor: "primary.main", borderRadius: 2 }}
            >
              <HubIcon />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1">
                Atlas Console
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 360 }}>
                Manage applications, releases, configuration, and shared workflows from one place.
              </Typography>
            </Box>
            <Button variant="contained" size="large" onClick={() => client.auth.login()} fullWidth>
              Sign in
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const tabs = TABS.filter((item) => !item.adminOnly || isAdmin);
  const active = tabs.find((item) => item.id === tab) || tabs[0];
  const Active = active.View;
  const identity = client.getIdentity && client.getIdentity();
  const primaryGroup = identity?.groups.find((group) => group !== "atlas-admins");
  const accountLabel = primaryGroup || (isAdmin ? "Atlas administrator" : "Signed in");
  const accountInitial = accountLabel.slice(0, 1).toUpperCase();
  const sections: { id: NavSection; label: string }[] = [
    { id: "workspace", label: "Workspace" },
    { id: "administration", label: "Administration" },
    { id: "settings", label: "Settings" },
  ];

  const navigate = (next: string) => {
    setTab(next);
    setMobileOpen(false);
  };

  const navigation = (
    <Stack sx={{ height: "100%", overflow: "hidden" }}>
      <Toolbar sx={{ minHeight: "72px !important", px: 2 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{ width: 38, height: 38, bgcolor: "primary.main", borderRadius: 1.25 }}
          >
            <HubIcon sx={{ fontSize: 22 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Atlas
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Control plane
            </Typography>
          </Box>
        </Stack>
      </Toolbar>
      <Divider />
      <Box
        component="nav"
        aria-label="Console navigation"
        sx={{ px: 1.25, py: 2, flexGrow: 1, overflowY: "auto" }}
      >
        <Stack spacing={2.25}>
          {sections.map((section) => {
            const sectionTabs = tabs.filter((item) => item.section === section.id);
            if (sectionTabs.length === 0) return null;
            return (
              <Box key={section.id}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 1.25,
                    mb: 0.75,
                    color: "text.secondary",
                    fontWeight: 600,
                    letterSpacing: "0.015em",
                  }}
                >
                  {section.label}
                </Typography>
                <List disablePadding>
                  {sectionTabs.map((item) => {
                    const selected = item.id === active.id;
                    const Icon = item.icon;
                    return (
                      <ListItemButton
                        key={item.id}
                        selected={selected}
                        aria-current={selected ? "page" : undefined}
                        onClick={() => navigate(item.id)}
                        sx={{
                          minHeight: 46,
                          mb: 0.4,
                          px: 1.25,
                          border: "1px solid transparent",
                          borderRadius: 1.25,
                          color: "text.secondary",
                          transition:
                            "background-color 140ms ease, border-color 140ms ease, color 140ms ease, box-shadow 140ms ease",
                          "&:hover": {
                            bgcolor: (value) =>
                              alpha(
                                value.palette.text.primary,
                                value.palette.mode === "dark" ? 0.07 : 0.04,
                              ),
                            color: "text.primary",
                          },
                          "&.Mui-selected": {
                            bgcolor: (value) =>
                              alpha(
                                value.palette.background.paper,
                                value.palette.mode === "dark" ? 0.78 : 0.88,
                              ),
                            borderColor: (value) => alpha(value.palette.divider, 0.92),
                            color: "text.primary",
                            boxShadow: (value) =>
                              `inset 0 1px 0 ${alpha(
                                value.palette.common.white,
                                value.palette.mode === "dark" ? 0.06 : 0.72,
                              )}, 0 5px 16px ${alpha(
                                value.palette.common.black,
                                value.palette.mode === "dark" ? 0.16 : 0.055,
                              )}`,
                            "&:hover": {
                              bgcolor: (value) =>
                                alpha(
                                  value.palette.background.paper,
                                  value.palette.mode === "dark" ? 0.86 : 0.96,
                                ),
                            },
                            "& .MuiListItemIcon-root": { color: "primary.main" },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                          <Icon sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: selected ? 600 : 500,
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            );
          })}
        </Stack>
      </Box>
      {onToggleColorMode && (
        <Box sx={{ px: 1.25, py: 1.25, borderTop: 1, borderColor: "divider" }}>
          <ListItemButton
            aria-label={colorMode === "dark" ? "Use light theme" : "Use dark theme"}
            onClick={onToggleColorMode}
            sx={{ minHeight: 48, px: 1.25, borderRadius: 1.25 }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: "text.secondary" }}>
              {colorMode === "dark" ? (
                <LightModeOutlinedIcon sx={{ fontSize: 20 }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary="Appearance"
              secondary={colorMode === "dark" ? "Dark mode" : "Light mode"}
              primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </ListItemButton>
        </Box>
      )}
      <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: (value) => alpha(value.palette.primary.main, 0.12),
              color: "primary.dark",
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {accountInitial}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {accountLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isAdmin ? "Admin access" : "Team access"}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <IconButton
              size="small"
              aria-label="Sign out"
              onClick={() => client.auth.logout()}
              sx={{ color: "text.secondary" }}
            >
              <LogoutIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ minHeight: "100dvh" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: (value) => value.zIndex.drawer + 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          borderBottom: 1,
          borderColor: (value) => alpha(value.palette.divider, 0.78),
          bgcolor: (value) =>
            alpha(value.palette.background.paper, value.palette.mode === "dark" ? 0.78 : 0.68),
          backgroundImage: (value) =>
            `linear-gradient(110deg, ${alpha(
              value.palette.background.paper,
              value.palette.mode === "dark" ? 0.12 : 0.32,
            )}, transparent 58%)`,
          backdropFilter: "blur(22px) saturate(120%)",
          WebkitBackdropFilter: "blur(22px) saturate(120%)",
          boxShadow: (value) =>
            `inset 0 -1px 0 ${alpha(
              value.palette.common.white,
              value.palette.mode === "dark" ? 0.04 : 0.48,
            )}`,
        }}
      >
        <Toolbar sx={{ minHeight: "72px !important", gap: 1.5 }}>
          {compact && (
            <IconButton
              edge="start"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {compact ? "Atlas Console" : active.label}
          </Typography>
          {primaryGroup && (
            <Chip
              size="small"
              label={primaryGroup}
              variant="outlined"
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            />
          )}
          {isAdmin && !primaryGroup && (
            <Chip
              size="small"
              label="Administrator"
              variant="outlined"
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            />
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={compact ? "temporary" : "permanent"}
        open={compact ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRightColor: (value) => alpha(value.palette.divider, 0.78),
            bgcolor: (value) =>
              alpha(value.palette.background.paper, value.palette.mode === "dark" ? 0.82 : 0.72),
            backgroundImage: (value) =>
              `linear-gradient(165deg, ${alpha(
                value.palette.primary.main,
                value.palette.mode === "dark" ? 0.055 : 0.035,
              )}, transparent 34%)`,
            backdropFilter: "blur(22px) saturate(118%)",
            WebkitBackdropFilter: "blur(22px) saturate(118%)",
            boxShadow: (value) =>
              `inset -1px 0 0 ${alpha(
                value.palette.common.white,
                value.palette.mode === "dark" ? 0.035 : 0.42,
              )}`,
          },
        }}
      >
        {navigation}
      </Drawer>

      <Box
        component="main"
        sx={{ ml: { md: `${DRAWER_WIDTH}px` }, pt: "72px", minWidth: 0 }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4.5 }, px: { xs: 2, sm: 3.5 } }}>
          <Box key={active.id} className="atlas-view-enter">
            <Active client={client} />
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
